import _                  from 'underscore';
import moment, { Moment } from 'moment';

import { usePrefStates }                  from './usePrefStates';
import { CorvalMilestone, Uuid }          from '../types';
import { OBJECTIVE_STATUS }               from '@src/constants';
import { TimeService }                    from '../services';
import { useFiltering }                   from './useFiltering';
import { ProjectTimelineDateFieldObject } from '../services/ProjectService';
import { useFeatureFlags }                from './useFeatureFlags';


export interface UseProjectObjectiveVisualizationHook {

    getDefaultFilters(): ProjectObjectivesMapFilterSetType,

    getMapFilters(): ProjectObjectivesMapFilterSetType

    hasFiltersApplied(): boolean

    generateYearlyMilestoneMapping(
        startDate: Moment,
        endDate: Moment,
        milestoneFields: ProjectTimelineDateFieldObject[]
    ): ObjectiveYearMappingItem[],

    focusYearlyMappingToRange(
        mapping: ObjectiveYearMappingItem[],
        startDate: Moment,
        endDate: Moment,
    ): ObjectiveYearMappingItem[]

    buildYearQuarterColumnDefs( yearMapping, currentYearsInQuarterView, minWidth ): string

    buildYearQuarterGridAreaDefs( yearMapping, currentYearsInQuarterView ): string

    generateYearQuarterKey( year: number | string, quarter?: number | string ): string

    extractYearQuarterFromKey( key: string, options?: { numericValues?: boolean } ): { year: string | number, quarter: string | number }
}

export interface ProjectObjectivesMapFilterSetType {
    businessFunctions: Uuid[],
    statuses: OBJECTIVE_STATUS[],
    categories: Uuid[],
    milestones: ProjectTimelineDateFieldObject[],
    tags: string[],
    hireTypes: Uuid[],
}

export type ProjectObjectivesMapFilterSetTypeKeys = keyof ProjectObjectivesMapFilterSetType;

export interface ObjectiveYearMappingItem {
    year: number,
    milestones: Array<ProjectTimelineDateFieldObject>,
}

export const ProjectObjectivesMapFilterSet: ProjectObjectivesMapFilterSetType = {
    businessFunctions: [],
    statuses:          [],
    categories:        [],
    milestones:        [],
    tags:              [],
    hireTypes:         [],
};

export const useProjectObjectiveVisualization = ( projectId: Uuid ): UseProjectObjectiveVisualizationHook => {

    let [ , prefs, prefEnums ] = usePrefStates( projectId ),
        filtering              = useFiltering(),
        featureFlags           = useFeatureFlags();

    let roadmapFilterKey = prefEnums.OBJECTIVES_ROADMAP_ACTIVE_FILTERS;

    const getDefaultFilters = () => {

        return _.clone( ProjectObjectivesMapFilterSet );
    };

    const getMapFilters: UseProjectObjectiveVisualizationHook['getMapFilters'] = () => {

        // do we have any saved filters?
        let userFilterPrefs = prefs.get( roadmapFilterKey );

        // get the default filters, with or w/o flagged items
        let defaultFilters = _.omit( getDefaultFilters(), _.compact( [

            !featureFlags.enableProjectActivityMetatagViewsAndTools ? 'tags' : null,

        ] ) );

        let activeFilters = _.mapObject( defaultFilters, ( value, filterKey ) => {

            if( _.isObject( userFilterPrefs ) ){

                if( !_.isEqual(
                    { f: userFilterPrefs[ filterKey ] },
                    { f: defaultFilters[ filterKey ] } )
                ){
                    if( !_.isUndefined( userFilterPrefs[ filterKey ] ) ){
                        return userFilterPrefs[ filterKey ];
                    }
                }
            }

            return value;

        } ) as ProjectObjectivesMapFilterSetType;

        return activeFilters;
    };

    const hasFiltersApplied: UseProjectObjectiveVisualizationHook['hasFiltersApplied'] = () => {

        let mapFilters         = getMapFilters(),
            defaultFilterPrefs = getDefaultFilters();

        // remove filter attributes to evaluate as active if flags aren't supporting them.
        let [ defaultFilterSet, currentFilterSubset ] = [ defaultFilterPrefs, mapFilters ]
            .map( filterSet => filtering.getFilterSubset<ProjectObjectivesMapFilterSetType>( filterSet, {

                tags: ( featureFlags.enableProjectActivityMetatagViewsAndTools === false ),

            } ) );

        let hasFiltersActive = !(
            filtering.isFilterSetDefaultSet( defaultFilterSet, currentFilterSubset ) ||
            filtering.isFilterSetSubsetOfDefaultSet( defaultFilterSet, currentFilterSubset )
        );

        return hasFiltersActive;
    };

    const generateYearlyMilestoneMapping: UseProjectObjectiveVisualizationHook['generateYearlyMilestoneMapping'] = ( startDate, endDate, dateFieldObjects = [] ) => {

        let groupedDateRanges = TimeService.getGroupsOfDatesByRange( startDate, endDate, 'year', 'years' ),
            dateFieldMoments  = dateFieldObjects.map( dfo => TimeService.localDateToMoment( dfo.rawValue ) ),
            startYearMoment   = startDate.clone().startOf( 'year' ),
            endYearMoment     = endDate.clone().startOf( 'year' ),
            startYear         = moment.min( [ startYearMoment, ...dateFieldMoments ] )?.year(),
            endYear           = moment.max( [ endYearMoment, ...dateFieldMoments ] )?.year(),
            yearsInDateRanges = _.range( endYear - startYear ).map( n => ( startYear + n ) );

        // clear the entries out
        let groupedDateFieldObjects = _.mapObject( groupedDateRanges, group => [] );

        // add in any years that dont have a group yet
        yearsInDateRanges.map( year => {
            if( !groupedDateFieldObjects[ year ] ){
                groupedDateFieldObjects[ year ] = [];
            }
        } );

        _.mapObject( dateFieldObjects, dateField => {

            let dateFieldMoment = TimeService.ISOtoMoment( dateField.rawValue ),
                dateFieldYear   = dateFieldMoment.year();

            if( groupedDateFieldObjects[ dateFieldYear ] ){

                groupedDateFieldObjects[ dateFieldYear ].push( dateField );

            } else {

                groupedDateFieldObjects[ dateFieldYear ] = [ dateField ];
            }
        } );

        return _.pairs( groupedDateFieldObjects ).map( ( [ year, milestones ] ) => ( { year: parseInt( year ), milestones } ) );
    };

    const focusYearlyMappingToRange: UseProjectObjectiveVisualizationHook['focusYearlyMappingToRange'] = ( mapping, startDate, endDate ) => {

        let focusedMapping = mapping;

        /* Both range dates specified */
        if( startDate && endDate ){

            focusedMapping = _.reject( focusedMapping, item => (
                ( item.year < startDate.year() ) ||
                ( item.year > endDate.year() )
            ) );
        }

        /* Just one year specified */
        if( startDate && !endDate ){
            focusedMapping = _.reject( focusedMapping, item => item.year !== startDate.year() );
        }

        /* Just one year specified */
        if( !startDate && endDate ){
            focusedMapping = _.reject( focusedMapping, item => item.year !== endDate.year() );
        }

        return focusedMapping;
    };

    const buildYearQuarterColumnDefs: UseProjectObjectiveVisualizationHook['buildYearQuarterColumnDefs'] = ( yearMapping, currentYearsInQuarterView, minWidth = 0 ) => {

        const columnWidth = `minmax(${ minWidth }px, 1fr)`;

        const gridColumns = yearMapping.map( ( { year } ) => {

            if( currentYearsInQuarterView.includes( year ) ){

                return _.range( 1, 5 ).map( q => columnWidth ).join( ' ' );

            } else {

                return columnWidth;
            }
        } );

        return gridColumns.join( ' ' );
    };

    const generateYearQuarterKey: UseProjectObjectiveVisualizationHook['generateYearQuarterKey'] = ( year, quarter = '' ) => {

        return `y${ year }${ _.isEmpty( quarter ) ? `q${ quarter }` : '' }`;
    };

    const extractYearQuarterFromKey: UseProjectObjectiveVisualizationHook['extractYearQuarterFromKey'] = ( value, _options = {} ) => {

        let [ year, quarter ] = value.replace( 'y', '' ).split( 'q' );

        const defaultOptions = { numericValues: true },
              options        = { ...defaultOptions, ..._options };

        year = !_.isEmpty( year ) ? year : null;
        quarter = !_.isEmpty( quarter ) ? quarter : null;

        return ( options?.numericValues )
               ? {
                year:    year ? parseInt( year ) : null,
                quarter: quarter ? parseInt( quarter ) : null
            } : {
                year:    year,
                quarter: quarter,
            };
    };

    const buildYearQuarterGridAreaDefs: UseProjectObjectiveVisualizationHook['buildYearQuarterGridAreaDefs'] = ( yearMapping, currentYearsInQuarterView ) => {

        const gridColumns = yearMapping.map( ( { year } ) => {

            if( currentYearsInQuarterView.includes( year ) ){

                return _.range( 1, 5 ).map( q => generateYearQuarterKey( year, q ) ).join( ' ' );

            } else {

                return generateYearQuarterKey( year );
            }
        } );

        return gridColumns.join( ' ' );
    };

    return {
        getDefaultFilters,
        getMapFilters,
        hasFiltersApplied,
        generateYearlyMilestoneMapping,
        focusYearlyMappingToRange,
        buildYearQuarterColumnDefs,
        buildYearQuarterGridAreaDefs,
        generateYearQuarterKey,
        extractYearQuarterFromKey,
    };
};
